import { required, maxLength } from 'vuelidate/lib/validators';
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import EdInputModel from '../../../../models/ed-input.model';
import EdFormModel from '../../../../models/ed-form.model';
import ToolService from '@/providers/tools';
import { Status } from '@/models/status.model';
import { Section } from '@/courses/models/section.model';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { CORE_ROUTES } from '@/core/core.router';
import { CourseToolsMixin } from '@/courses/mixins/course-tools.mixin';
import {IParamsCreateComponent} from '@/courses/interfaces/params-create-component.interface';

@Component({
  name: 'ed-deck-library-creator',
  components: {
    EdSelectComponentCourse: () => import(/* EdSelectComponentCourse */ '@/course-templates/components/ed-select-component-course/ed-select-component-course.component.vue')
  }
})
export default class EdDeckLibraryCreator extends Mixins(CourseToolsMixin) {

  @Prop() readonly courseId: number;
  @Prop() readonly classId: number;
  @Prop() readonly isTemplate: boolean;

  public form: any = null;
  public saving: boolean = false;
  public status: Status = new Status()

  get name(): string {
    if (this.deckSection && this.editionMode) {
      return ToolService.exitSpecialChars(this.deckSection.name);
    } else {
      return this.$i18n.t('sections.deckLibrary').toString();
    }
  }

  get title() {
    if(this.editionMode) {
      return `${this.labelTitles.edit}: ${this.name}`; 
    } else {
      return `${this.labelTitles.addTo} ${this.name}`;
    }
  }

  get labelTitles() {
    return {
      addTo: this.$i18n.t('deck.addTitle'),
      edit: this.$i18n.t('deck.editTitle'),
    }
  }

  get course() {
    return this.getCourseFromStore({courseId: this.courseId , classId: this.classId});
  }

  get editionMode() {
    return this.course.component.edition;
  }

  get deckLibrary(){
    return this.course.deckLibrary;
  }

  get deckSection(): Section {
    if(this.course.component.section) {
      return this.course.component.section;
    }
    return null;
  }

  get sectionId() {
    return this.deckSection ? this.deckSection.sectionId : null;
  }

  get isDisabled(): boolean {
    return !this.form.name.valid || this.saving;
  }

  get suffixName(): string {
    if (this.editionMode) {
      return this.$i18n.t(
        `deck.cartType.${this.deckSection.text.toLocaleLowerCase()}`
      ).toString();
    }

    return '';
  }

  get components() {
    return this.course.eduacComponents;
  }

  get componentSelected() : IParamsCreateComponent {
    return this.$store.state.CourseStore.eduacComponentSelected;
  }

  
  set componentSelected(newValue : IParamsCreateComponent) {
    this.$Courses.Store.setEduacComponentSelected(newValue);
  }

  init(name: string = '', cardType: string = 'BOTH') {
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(60) }, !!name),
      EdInputModel('cardType', cardType, { required }, true)
    ]);

    setTimeout(() => {
      this.resetValidations();
    }, 3);
  }

  resetValidations() {
    this.$root.$emit('reset-text-field-validation');
  }

  goBack() {
    const name = this.isTemplate ? CORE_ROUTES.CREATE_COURSE_TEMPLATE.NAME : CORE_ROUTES.CREATE_COURSE.NAME;
    this.$router.push({
      name,
      params: {
        id: this.course.id
      }
    });
  }

  async createDeck() {
    if(this.editionMode) {
      await this.updateDeck();
    } else {
      await this.saveDeck();
    }
  }

  async saveDeck() {
    try {
      this.saving = true;
      let id = await this.$Courses.Store.createDeckA( {
        contentId: this.courseId,
        classId: this.classId,
        deckName: this.form.name.value,
        cardType: this.form.cardType.value,
        component: this.componentSelected
      });
      this.form.set('name', '');
      this.saving = false;
    } catch(err) {
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.errors.errorSavingObject')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
      this.saving = false;
    }
  }

  async updateDeck() {
    let data: any = { 
      contentId: this.courseId,
      deckSectionId: this.sectionId,
      deckName: this.form.name.value,
      component: this.componentSelected
     };
    this.saving = true;
    try{
      await this.$Courses.Store.updateDeckA(data);
    } catch(error){
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.errors.errorSavingObject')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    } finally {
      this.saving = false;
    }
  }

  setData() {
    this.status.setLoading();
    if(this.editionMode && this.deckSection) {
      this.init(this.deckSection.name, this.deckSection.text);
      if(this.deckSection.component && this.components?.length) {
        this.componentSelected = this.components.find(c => c.id == this.deckSection.component.id);
      } else if (this.components?.length) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    } else {
      this.init();
      if(this.course.eduacComponents) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    }
    this.status.setLoad();
  }

  changeComponent(data) {
    this.componentSelected = data;
  }

  created() {
    this.setData();
  }

  @Watch('deckSection', { deep: true, immediate: false })
  sectionChanged() {
    this.setData();
    setTimeout(() => {
      this.resetValidations();
    }, 100);
  }
}
